import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { getBillingInfo, getInvoices, markInvoicePaid, type BillingInfo, type Invoice } from '@/api/config'
import { useAuth } from '@/hooks/useAuth'
import {
  Receipt, TrendingUp, CalendarDays, ChevronRight,
  CheckCircle2, AlertTriangle, Loader2, Clock, CreditCard, Ban, ArrowRight, Gift,
} from 'lucide-react'

function formatCOP(n: number) {
  return `$${Number(n).toLocaleString('es-CO')}`
}

function billingPeriod() {
  const now   = new Date()
  const year  = now.getFullYear()
  const month = now.getMonth()
  const start = new Date(year, month, 1)
  const end   = new Date(year, month + 1, 0)
  const next  = new Date(year, month + 1, 1)
  const fmt     = (d: Date) => d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })
  const fmtFull = (d: Date) => d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
  return {
    label:    `${fmt(start)} – ${fmtFull(end)}`,
    cutoff:   fmtFull(next),
    daysLeft: Math.ceil((end.getTime() - now.getTime()) / 86_400_000),
  }
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
}

function periodLabel(iso: string) {
  return new Date(iso).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
    .replace(/^\w/, c => c.toUpperCase())
}

const STATUS_META: Record<Invoice['status'], { label: string; color: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pendiente',
    color: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/25',
    icon:  <Clock className="w-3 h-3" />,
  },
  overdue: {
    label: 'Vencida',
    color: 'text-red-500 bg-red-500/10 border-red-500/25',
    icon:  <AlertTriangle className="w-3 h-3" />,
  },
  paid: {
    label: 'Pagada',
    color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
    icon:  <CheckCircle2 className="w-3 h-3" />,
  },
  void: {
    label: 'Anulada',
    color: 'text-text-muted bg-background border-border',
    icon:  <Ban className="w-3 h-3" />,
  },
}

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const m = STATUS_META[status]
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${m.color}`}>
      {m.icon} {m.label}
    </span>
  )
}

export function BillingTab() {
  const navigate                    = useNavigate()
  const { user }                    = useAuth()
  const isSuperadmin                = user?.role === 'superadmin'
  const [info, setInfo]             = useState<BillingInfo | null>(null)
  const [invoices, setInvoices]     = useState<Invoice[]>([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(false)
  const [paying, setPaying]         = useState<string | null>(null)
  const period                      = billingPeriod()

  useEffect(() => {
    Promise.allSettled([getBillingInfo(), getInvoices()])
      .then(([billingRes, invoicesRes]) => {
        if (billingRes.status === 'fulfilled') setInfo(billingRes.value)
        else setError(true)
        if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value.invoices)
      })
      .finally(() => setLoading(false))
  }, [])

  async function handleMarkPaid(inv: Invoice) {
    setPaying(inv.id)
    try {
      await markInvoicePaid(inv.id, { total_cop: info?.costs.total })
      setInvoices(prev =>
        prev.map(i => i.id === inv.id ? { ...i, status: 'paid', paid_at: new Date().toISOString() } : i)
      )
      toast.success(`Factura de ${periodLabel(inv.period_start)} marcada como pagada`)
    } catch {
      toast.error('Error al actualizar la factura')
    } finally {
      setPaying(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
      </div>
    )
  }

  if (error || !info) {
    return (
      <div className="bg-surface border border-border rounded-2xl p-8 text-center">
        <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
        <p className="text-text-primary font-semibold text-sm">No se pudo cargar la información de facturación</p>
        <p className="text-text-muted text-xs mt-1">Verifica tu conexión e intenta de nuevo.</p>
      </div>
    )
  }

  const hasOverage   = info.costs.extra_devices > 0 || info.costs.extra_members > 0 || info.costs.extra_users > 0
  const gracePeriod  = info.in_grace_period
  const billingStart = info.billing_start
    ? new Date(info.billing_start).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  return (
    <div className="flex flex-col gap-6">

      {/* Banner período de gracia */}
      {gracePeriod && billingStart && (
        <div className="flex items-start gap-3 bg-emerald-500/8 border border-emerald-500/25 rounded-2xl px-5 py-4">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center shrink-0 mt-0.5">
            <Gift className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <p className="text-emerald-600 dark:text-emerald-400 text-sm font-bold">Período de prueba gratuito</p>
            <p className="text-text-muted text-xs mt-0.5">
              Estás usando el sistema de forma gratuita. La facturación comienza el <span className="font-semibold text-text-primary">{billingStart}</span>.
            </p>
          </div>
        </div>
      )}

      {/* Período + corte + total */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-2 text-text-muted mb-1">
            <CalendarDays className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Período actual</span>
          </div>
          <p className="text-text-primary text-sm font-bold leading-snug">{period.label}</p>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-2 text-text-muted mb-1">
            <Receipt className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">
              {gracePeriod ? 'Inicio de facturación' : 'Próximo corte'}
            </span>
          </div>
          <p className="text-text-primary text-sm font-bold leading-snug">
            {gracePeriod && billingStart ? billingStart : period.cutoff}
          </p>
          {!gracePeriod && <p className="text-text-muted text-[10px]">{period.daysLeft} días restantes</p>}
        </div>

        <div className={`bg-surface border rounded-2xl p-4 flex flex-col gap-1 col-span-2 sm:col-span-1 ${gracePeriod ? 'border-emerald-500/25' : hasOverage ? 'border-amber-500/30' : 'border-border'}`}>
          <div className="flex items-center gap-2 text-text-muted mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Total estimado</span>
          </div>
          {gracePeriod ? (
            <>
              <p className="text-emerald-500 text-xl font-black leading-tight">Gratis</p>
              <p className="text-text-muted text-[10px]">hasta el {billingStart}</p>
            </>
          ) : (
            <>
              <p className={`text-xl font-black leading-tight tabular-nums ${hasOverage ? 'text-amber-500' : 'text-accent'}`}>
                {formatCOP(info.costs.total)}
              </p>
              <p className="text-text-muted text-[10px]">COP / mes</p>
            </>
          )}
        </div>
      </div>

      {/* Desglose del mes */}
      <section className="flex flex-col gap-3">
        <h2 className="text-text-muted text-xs font-bold uppercase tracking-widest">Desglose del mes</h2>
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">

          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border bg-accent/3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
                <Receipt className="w-4 h-4 text-accent" />
              </div>
              <div>
                <p className="text-text-primary text-sm font-bold">Plan base</p>
                <p className="text-text-muted text-[11px] mt-0.5">
                  Incluye {info.plan.included_devices} disp. · {info.plan.included_members} miembros · {info.plan.included_users} usuarios
                </p>
              </div>
            </div>
            {gracePeriod ? (
              <span className="text-emerald-500 font-black text-sm shrink-0">Gratis</span>
            ) : (
              <p className="text-accent font-black text-base tabular-nums shrink-0">{formatCOP(info.costs.base)}</p>
            )}
          </div>

          {[
            {
              qty:   info.overage.extra_devices,
              label: 'Dispositivos adicionales',
              sub:   `${info.overage.extra_devices} × ${formatCOP(info.prices.extra_device_cop)} por dispositivo`,
              cost:  info.costs.extra_devices,
            },
            {
              qty:   info.overage.extra_member_blocks,
              label: 'Bloques de 200 miembros adicionales',
              sub:   `${info.overage.extra_member_blocks} × ${formatCOP(info.prices.extra_members_block_cop)} por bloque`,
              cost:  info.costs.extra_members,
            },
            {
              qty:   info.overage.extra_users,
              label: 'Usuarios admin adicionales',
              sub:   `${info.overage.extra_users} × ${formatCOP(info.prices.extra_user_cop)} por usuario`,
              cost:  info.costs.extra_users,
            },
          ].map((row, i) => (
            <div key={i} className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${row.qty > 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-background border border-border'}`}>
                  <ChevronRight className={`w-4 h-4 ${row.qty > 0 ? 'text-amber-500' : 'text-text-muted'}`} />
                </div>
                <div>
                  <p className="text-text-primary text-sm font-semibold">{row.label}</p>
                  <p className="text-text-muted text-[11px] mt-0.5">{row.sub}</p>
                </div>
              </div>
              {gracePeriod ? (
                <span className="text-emerald-500 font-bold text-sm shrink-0">Gratis</span>
              ) : (
                <p className={`font-bold text-sm tabular-nums shrink-0 ${row.qty > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-text-muted'}`}>
                  {row.qty > 0 ? `+${formatCOP(row.cost)}` : formatCOP(0)}
                </p>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between gap-4 px-5 py-4 bg-background/40">
            <p className="text-text-primary text-sm font-bold">Total mensual</p>
            {gracePeriod ? (
              <p className="text-emerald-500 text-lg font-black">
                Gratis <span className="text-text-muted text-xs font-normal">hasta {billingStart}</span>
              </p>
            ) : (
              <p className={`text-lg font-black tabular-nums ${hasOverage ? 'text-amber-500' : 'text-accent'}`}>
                {formatCOP(info.costs.total)} <span className="text-text-muted text-xs font-normal">COP</span>
              </p>
            )}
          </div>
        </div>

        {!info.enforcement && (
          <p className="text-text-muted text-xs flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            Los límites del plan están desactivados — no se aplican cargos por excedente actualmente.
          </p>
        )}
      </section>

      {/* Historial de facturas */}
      <section className="flex flex-col gap-3">
        <h2 className="text-text-muted text-xs font-bold uppercase tracking-widest">Historial de facturas</h2>

        {invoices.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-8 flex flex-col items-center gap-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-accent/10 border border-accent/15 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="text-text-primary text-sm font-semibold">Sin facturas generadas</p>
              <p className="text-text-muted text-xs mt-1 max-w-xs mx-auto">
                {isSuperadmin
                  ? 'Configura la fecha de inicio de facturación para comenzar a generar facturas automáticamente.'
                  : 'Las facturas aparecerán aquí una vez que el proveedor configure la fecha de inicio de facturación.'}
              </p>
            </div>
            {isSuperadmin && (
              <button
                onClick={() => navigate('/admin/config')}
                className="flex items-center gap-2 px-4 py-2.5 bg-accent text-background text-xs font-bold rounded-xl hover:opacity-90 transition-opacity cursor-pointer"
              >
                Ir a Configuración del sistema
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : (
          <div className="bg-surface border border-border rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-4 px-5 py-2.5 border-b border-border bg-background/40">
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Período</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted text-right">Estado</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted text-right">Vencimiento</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted text-right">Total</span>
            </div>

            {invoices.map((inv, idx) => {
              const isLast     = idx === invoices.length - 1
              const canPay     = isSuperadmin && (inv.status === 'pending' || inv.status === 'overdue')
              const isPaying   = paying === inv.id

              return (
                <div
                  key={inv.id}
                  className={`flex flex-col sm:grid sm:grid-cols-[1fr_auto_auto_auto] sm:items-center gap-2 sm:gap-4 px-5 py-4 ${!isLast ? 'border-b border-border' : ''}`}
                >
                  {/* Period */}
                  <div>
                    <p className="text-text-primary text-sm font-bold">{periodLabel(inv.period_start)}</p>
                    <p className="text-text-muted text-[10px] mt-0.5">
                      {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                    </p>
                    {inv.paid_at && (
                      <p className="text-emerald-600 dark:text-emerald-400 text-[10px] mt-0.5">
                        Pagada el {fmtDate(inv.paid_at)}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex sm:justify-end">
                    <StatusBadge status={inv.status} />
                  </div>

                  {/* Due date */}
                  <p className="text-text-muted text-xs text-right hidden sm:block">
                    {fmtDate(inv.due_date)}
                  </p>

                  {/* Amount + action */}
                  <div className="flex items-center justify-between sm:justify-end gap-3">
                    <p className={`font-black text-sm tabular-nums ${inv.status === 'paid' ? 'text-text-primary' : inv.status === 'overdue' ? 'text-red-500' : 'text-text-primary'}`}>
                      {formatCOP(inv.total_cop)}
                    </p>
                    {canPay && (
                      <button
                        onClick={() => handleMarkPaid(inv)}
                        disabled={isPaying}
                        className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                      >
                        {isPaying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        {isPaying ? 'Guardando…' : 'Marcar pagada'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

    </div>
  )
}
